import {
  getUlrTests,
  getUrlIndicatorCalculateResults,
  modeAuthorized,
  runLocalTest,
} from '../spdConfig';

import { Loader } from './loader';
import { SurveyManager } from './surveyManager';
import * as Configuration from './testConfiguration';
import { TextId } from './textSelector';
import { TimersManager } from './timersManager';
import { FilePreviewManager } from './filePreview';
import { IndicatorTestResult } from './results';

declare let testConfiguration: Configuration.TestConfiguration;

// API głównie dla enginów do pluginów testów
export class SPDEngineGateway {
  protected surveyManager: SurveyManager;
  private filePreviewManager = new FilePreviewManager();
  private readonly maxFileSizeBytes = 10 * 1024 * 1024;

  constructor() {
    this.surveyManager = new SurveyManager();
  }
  public doNextTask(taskNumber: string, code: string | null) {
    this.surveyManager.doNextTask(taskNumber, code);
  }

  public doPrevTask(taskNumber: string, code: string) {
    this.surveyManager.doPrevTask(taskNumber, code);
  }

  public doAction(taskNumber: string, code: string, param?: string): boolean {
    return this.surveyManager.doAction(taskNumber, code, param);
  }

  public async doTest(taskNumber: string, code: string, param?: number) {
    await this.surveyManager.doTest(taskNumber, code, param);
  }

  public async doTestIdent(taskNumber: string, code: string, param?: string) {
    await this.surveyManager.doTestIdent(taskNumber, code, param);
  }

  public onInput() {
    this.surveyManager.onInput();
  }

  public chooseFile(taskNumber: string, code: string, param?: string) {
    void taskNumber;
    const targetCode = param || code;
    const elem = document.querySelector(
      '[answer="' + targetCode + '"]',
    ) as HTMLInputElement | null;
    if (!elem || elem.type !== 'file') {
      return;
    }
    elem.click();
  }

  public onFileSelected(taskNumber: string, code: string, param?: string) {
    void taskNumber;
    void param;
    const elem = document.querySelector(
      '[answer="' + code + '"]',
    ) as HTMLInputElement | null;
    if (!elem || elem.type !== 'file') {
      return;
    }

    const files = elem.files;
    if (!files || files.length === 0) {
      this.revokePreviewUrl(elem);
      elem.dataset.filePreviewUrl = '';
      elem.dataset.filePreviewType = '';
      elem.dataset.fileContent = '';
      elem.dataset.fileName = '';
      this.onInput();
      return;
    }

    const file = files[0];
    if (file.size > this.maxFileSizeBytes) {
      elem.value = '';
      this.revokePreviewUrl(elem);
      elem.dataset.filePreviewUrl = '';
      elem.dataset.filePreviewType = '';
      elem.dataset.fileContent = '';
      elem.dataset.fileName = '';
      void this.filePreviewManager.updateFromInput(elem);
      this.onInput();
      this.hideShowAfterChangeInput();
      this.showTaskHint(1);
      return;
    }
    const isPdf =
      file.type === 'application/pdf' ||
      file.name.toLowerCase().endsWith('.pdf');
    if (isPdf) {
      this.revokePreviewUrl(elem);
      elem.dataset.filePreviewUrl = URL.createObjectURL(file);
      elem.dataset.filePreviewType = 'application/pdf';
      void this.filePreviewManager.updateFromInput(elem);
    } else {
      this.revokePreviewUrl(elem);
      elem.dataset.filePreviewUrl = '';
      elem.dataset.filePreviewType = '';
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      elem.dataset.fileContent = result;
      elem.dataset.fileName = file.name;
      void this.filePreviewManager.updateFromInput(elem);
      this.clearTaskHint();
      this.onInput();
    };
    reader.onerror = () => {
      this.revokePreviewUrl(elem);
      elem.dataset.filePreviewUrl = '';
      elem.dataset.filePreviewType = '';
      elem.dataset.fileContent = '';
      elem.dataset.fileName = '';
      void this.filePreviewManager.updateFromInput(elem);
      this.onInput();
    };
    reader.readAsDataURL(file);
  }

  public clearSelectedFile(taskNumber: string, code: string, param?: string) {
    void taskNumber;
    const targetCode = param || code;
    const elem = document.querySelector(
      '[answer="' + targetCode + '"]',
    ) as HTMLInputElement | null;
    if (!elem || elem.type !== 'file') {
      return;
    }
    elem.value = '';
    this.revokePreviewUrl(elem);
    elem.dataset.filePreviewUrl = '';
    elem.dataset.filePreviewType = '';
    elem.dataset.fileContent = '';
    elem.dataset.fileName = '';
    void this.filePreviewManager.updateFromInput(elem);
    this.onInput();
    this.hideShowAfterChangeInput();
  }

  public updateFilePreview(code: string) {
    const elem = document.querySelector(
      '[answer="' + code + '"]',
    ) as HTMLInputElement | null;
    if (!elem || elem.type !== 'file') {
      return;
    }
    void this.filePreviewManager.updateFromInput(elem);
  }

  protected hideShowAfterChangeInput() {
    const elements = document.querySelectorAll('.show-after-change-input');
    elements.forEach(elem => {
      (elem as HTMLElement).style.display = 'none';
    });
  }

  public switchCamera(taskNumber: string, code: string, param?: string) {
    this.surveyManager.switchCamera(taskNumber, code, param);
  }

  public async setGender(gender: string) {
    await this.surveyManager.setGender(gender);
  }

  public async startSurvey() {
    await this.surveyManager.startSurvey();
  }

  public getTemplateParameters() {
    return this.surveyManager.getTemplateParameters();
  }

  public getIndicatorResultsPayload() {
    return this.surveyManager.getIndicatorResultsPayload();
  }

  public getIndicatorTestFileName() {
    return this.surveyManager.getIndicatorTestFileName();
  }

  public async calculateIndicatorResults(): Promise<IndicatorTestResult[]> {
    const testFileName = this.getIndicatorTestFileName();
    const payload = this.getIndicatorResultsPayload();
    if (!testFileName || !payload) {
      return [];
    }
    const url =
      getUrlIndicatorCalculateResults() +
      '?testFileName=' +
      encodeURIComponent(testFileName);
    const response = ((await Loader.postJsonSync(url, payload, true)) ||
      []) as IndicatorTestResult[];
    return response;
  }

  public clearPicture(taskNumber: string, code: string, param?: string) {
    this.surveyManager.clearPicture(taskNumber, code, param);
  }

  public takePhoto(taskNumber: string, code: string, param?: string) {
    this.surveyManager.takePhoto(taskNumber, code, param);
  }

  public async doEndSurvey() {
    await this.surveyManager.doEndSurvey();
  }

  public getTextPresentation(textId: TextId) {
    return this.surveyManager.getTextPresentation(textId);
  }

  public async getJSON(url: string) {
    try {
      return await Loader.getSyncJSONWithVersion(getUlrTests() + url);
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  public async playSoundOrSpeakText(textId: TextId, must_read = true) {
    await this.surveyManager.playSoundOrSpeakText(textId, must_read);
  }

  public getTimersManager() {
    return new TimersManager();
  }

  public setReactionStartTime() {
    this.surveyManager.setReactionStartTime();
  }

  public setLatencyStartTime(latencyTime: number) {
    this.surveyManager.setLatencyStartTime(latencyTime);
  }

  public setDecisionTime() {
    this.surveyManager.setDecisionTime();
  }

  public getGender() {
    return testConfiguration.gender;
  }

  public getDateOfBirth() {
    return this.surveyManager.getDateOfBirth();
  }

  public getSurveyNumber() {
    return this.surveyManager.getSurveyNumber();
  }

  public setOriginalValue(value: string) {
    this.surveyManager.setOriginalValue(value);
  }

  public getValue() {
    return this.surveyManager.getValue();
  }

  public getValueFromTask(taskNumber: string) {
    return this.surveyManager.getValueFromTask(taskNumber);
  }

  public showTaskHint(nr: number) {
    this.surveyManager.showTaskHint(nr);
  }

  public clearTaskHint() {
    this.surveyManager.clearTaskHint();
  }

  public playClick() {
    this.surveyManager.playClick();
  }

  public playGoodClick() {
    this.surveyManager.playGoodClick();
  }

  public playBadClick() {
    this.surveyManager.playBadClick();
  }

  public setSkipNextClickSound() {
    this.surveyManager.setSkipNextClickSound();
  }

  public reloadTask() {
    this.surveyManager.reloadTask();
  }

  public verifyOnline() {
    this.surveyManager.verifyOnline();
  }

  public clearAllTimers() {
    this.surveyManager.clearAllTimers();
  }

  public runLocalTestWithoutAuth() {
    return runLocalTest() && !modeAuthorized();
  }

  private revokePreviewUrl(elem: HTMLInputElement) {
    const previewUrl = elem.dataset.filePreviewUrl;
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
  }
}
